import { formDataQueryOptions, useFormDataQuery } from '@/api/caseCard/getters';
import ChatMessage from '@/components/ChatMessage';
import Instruction from '@/components/Instruction';
import Layout from '@/components/layouts/Layout';
import { CASE_CARD_PRODUCT, ERROR_MESSAGES, WebsiteType } from '@/consts';
import DetailsDialog from '@/features/caseCard/components/DetailsDialog';
import { Uploader } from '@/features/caseCard/components/Uploader';
import { getCaseCardContent } from '@/features/caseCard/content';
import { CaseCardVariants } from '@/features/v2/MobileMenu';

import DevtoolsCaseCard from './Devtools';
import { QueryClient } from '@tanstack/react-query';
import { LoaderFunctionArgs, useParams } from 'react-router-dom';

export const AppLoaderCaseCard =
  (queryClient: QueryClient) => async (props: LoaderFunctionArgs) => {
    const { params } = props;
    const id = params?.id as string;

    try {
      const query = formDataQueryOptions(id);
      const data = await queryClient.ensureQueryData(query);

      return data;
    } catch (e) {
      const query = formDataQueryOptions(id);

      if (e instanceof Response) {
        throw e;
      }

      if (import.meta.env.VITE_SHOW_LOGS === 'true') {
        const data = (await queryClient.ensureQueryData(query)) as unknown as {
          message: string;
        };

        throw new Response(data.message ?? ERROR_MESSAGES.API_DOWN, {
          status: 503,
        });
      }

      throw new Response(ERROR_MESSAGES.API_DOWN, {
        status: 503,
      });
    }
  };

const AppCaseCard = () => {
  const { id } = useParams();
  const { data } = useFormDataQuery();

  if (!data) return null;

  const content = getCaseCardContent(data.product_id, data.site);

  const variant =
    data.product_id === CASE_CARD_PRODUCT.DRYING
      ? WebsiteType.DEEPRY
      : WebsiteType.HELPFIND;

  const showDevtools = import.meta.env.VITE_SHOW_DEV_TOOLS === 'true';

  return (
    <Layout
      data={data}
      withoutProgress
      caseCard
      variant={variant}
      navbarProps={{
        ...content.navbar,
        disableMobileMenu: true,
        caseCardVariant: CaseCardVariants.EMPTY,
        withoutPhone: !content.navbar.phone,
      }}
      className="[&>main>div]:space-y-10 [&>main>div]:lg:space-y-10 [&>main]:pb-[60px] [&>main]:lg:pt-[30px]"
    >
      <div>
        <ChatMessage
          {...content.chatMessage}
          variant="primary"
          className="items-start lg:flex-col lg:px-0 lg:pt-0 [&>div>button]:hidden [&>p]:max-w-full"
        />
        <Uploader
          variant={variant}
          title="Dodaj załączniki"
          btnDescription={
            content.btnDescription ||
            'Akceptowane pliki: .jpg, .pdf, .jfif, .png, .heic, .zip, oraz .7zip.'
          }
          content={content}
        />
      </div>
      {content?.instruction && (
        <div className="rounded-lg bg-gray-100 px-3.5 py-4 lg:p-6">
          <Instruction
            {...content.instruction}
            className="[&>div>div>div>div>img]:rounded-sm [&>div>div>div>div>img]:object-cover [&>div>div>div>div>img]:sm:h-[200px] [&>div>div>div>span>svg]:size-3 [&>div>div>div>span]:p-0.5 [&>div>div>div>span]:lg:p-1 [&>div>div>div]:sm:h-[216px] [&>div>div]:sm:h-[216px] [&>div]:sm:flex-col"
          />
        </div>
      )}
      {content.detailsDialog && (
        // Keyed by case, so another case opened in the same session starts
        // with an empty form.
        <DetailsDialog key={id} consultant={content.chatMessage.consultant} />
      )}
      {showDevtools && (
        <>
          <DevtoolsCaseCard />
        </>
      )}
    </Layout>
  );
};

export default AppCaseCard;
